/**
 * Built-in value-node implementations with no viewer or scene dependencies.
 * Each implementation maps input-port values to output-port values.
 */
function num(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}
function bool(value, fallback = false) {
    if (typeof value === 'boolean')
        return value;
    if (value === 'true')
        return true;
    if (value === 'false')
        return false;
    return fallback;
}
function str(value, fallback = '') {
    if (value == null)
        return fallback;
    return String(value);
}
function vec(value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const v = value;
        return { x: num(v.x), y: num(v.y), z: num(v.z) };
    }
    return { x: 0, y: 0, z: 0 };
}
const implementations = new Map();
function addImplementation(type, implementation, options) {
    implementations.set(type, { implementation, isVolatile: options?.isVolatile });
}
function addMathImplementations() {
    addImplementation('add', (i) => ({ result: num(i.a) + num(i.b) }));
    addImplementation('subtract', (i) => ({ result: num(i.a) - num(i.b) }));
    addImplementation('multiply', (i) => ({ result: num(i.a) * num(i.b) }));
    addImplementation('divide', (i) => {
        const b = num(i.b, 1);
        return { result: b === 0 ? 0 : num(i.a) / b };
    });
    addImplementation('increment', (i) => ({ result: num(i.value) + 1 }));
    addImplementation('decrement', (i) => ({ result: num(i.value) - 1 }));
    addImplementation('sign', (i) => {
        const value = num(i.value);
        if (value > 0)
            return { result: 1 };
        if (value < 0)
            return { result: -1 };
        return { result: 0 };
    });
    addImplementation('nearlyEqual', (i) => {
        const tolerance = Math.abs(num(i.errorTolerance, 0.0001));
        return { result: Math.abs(num(i.a) - num(i.b)) <= tolerance };
    });
    addImplementation('modulo', (i) => {
        const b = num(i.b, 1);
        return { result: b === 0 ? 0 : num(i.a) % b };
    });
    addImplementation('power', (i) => ({ result: num(i.base, 1) ** num(i.exponent, 2) }));
    addImplementation('sqrt', (i) => {
        const v = num(i.value, 1);
        return { result: v < 0 ? 0 : Math.sqrt(v) };
    });
    addImplementation('abs', (i) => ({ result: Math.abs(num(i.value)) }));
    addImplementation('negate', (i) => ({ result: -num(i.value) }));
    addImplementation('min', (i) => ({ result: Math.min(num(i.a), num(i.b)) }));
    addImplementation('max', (i) => ({ result: Math.max(num(i.a), num(i.b)) }));
    addImplementation('clamp', (i) => {
        const value = num(i.value);
        const min = num(i.min);
        const max = num(i.max);
        return { result: Math.min(max, Math.max(min, value)) };
    });
    addImplementation('lerp', (i) => {
        const a = num(i.a);
        const b = num(i.b, 1);
        const alpha = num(i.alpha, 0.5);
        return { result: a + (b - a) * alpha };
    });
    addImplementation('mapRange', (i) => {
        const value = num(i.value);
        const inMin = num(i.inMin);
        const inMax = num(i.inMax, 1);
        const outMin = num(i.outMin);
        const outMax = num(i.outMax, 100);
        const span = inMax - inMin;
        if (span === 0)
            return { result: outMin };
        const t = (value - inMin) / span;
        return { result: outMin + t * (outMax - outMin) };
    });
    addImplementation('round', (i) => ({ result: Math.round(num(i.value)) }));
    addImplementation('floor', (i) => ({ result: Math.floor(num(i.value)) }));
    addImplementation('ceil', (i) => ({ result: Math.ceil(num(i.value)) }));
    addImplementation('sin', (i) => ({ result: Math.sin(num(i.angle)) }));
    addImplementation('cos', (i) => ({ result: Math.cos(num(i.angle)) }));
    addImplementation('tan', (i) => ({ result: Math.tan(num(i.angle)) }));
    addImplementation('degToRad', (i) => ({ result: (num(i.value) * Math.PI) / 180 }));
    addImplementation('radToDeg', (i) => ({ result: (num(i.value) * 180) / Math.PI }));
    addImplementation('random', (i) => {
        const min = num(i.min);
        const max = num(i.max, 1);
        return { result: min + Math.random() * (max - min) };
    }, { isVolatile: true });
    addImplementation('randomInt', (i) => {
        const min = Math.ceil(num(i.min));
        const max = Math.floor(num(i.max, 100));
        return { result: Math.floor(Math.random() * (max - min + 1)) + min };
    }, { isVolatile: true });
    addImplementation('randomBool', () => ({ result: Math.random() < 0.5 }), { isVolatile: true });
    addImplementation('pi', () => ({ result: Math.PI }));
}
function addComparisonImplementations() {
    addImplementation('equal', (i) => ({ result: num(i.a) === num(i.b) }));
    addImplementation('notEqual', (i) => ({ result: num(i.a) !== num(i.b) }));
    addImplementation('greater', (i) => ({ result: num(i.a) > num(i.b) }));
    addImplementation('less', (i) => ({ result: num(i.a) < num(i.b) }));
    addImplementation('greaterEqual', (i) => ({ result: num(i.a) >= num(i.b) }));
    addImplementation('lessEqual', (i) => ({ result: num(i.a) <= num(i.b) }));
    addImplementation('inRange', (i) => {
        const value = num(i.value);
        return { result: value >= num(i.min) && value <= num(i.max, 1) };
    });
}
function addBooleanImplementations() {
    addImplementation('and', (i) => ({ result: bool(i.a) && bool(i.b) }));
    addImplementation('or', (i) => ({ result: bool(i.a) || bool(i.b) }));
    addImplementation('not', (i) => ({ result: !bool(i.value) }));
    addImplementation('xor', (i) => ({ result: bool(i.a) !== bool(i.b) }));
    addImplementation('nand', (i) => ({ result: !(bool(i.a) && bool(i.b)) }));
    addImplementation('nor', (i) => ({ result: !(bool(i.a) || bool(i.b)) }));
}
function addFlowImplementations() {
    addImplementation('select', (i) => ({ result: bool(i.pick, true) ? i.a : i.b }));
    addImplementation('isValid', (i) => {
        const value = i.value;
        if (value == null)
            return { result: false };
        if (typeof value !== 'object')
            return { result: true };
        return { result: Object.keys(value).length > 0 };
    });
}
function addVectorImplementations() {
    addImplementation('makeVector', (i) => ({
        result: { x: num(i.x), y: num(i.y), z: num(i.z) }
    }));
    addImplementation('breakVector', (i) => {
        const v = vec(i.value);
        return { x: v.x, y: v.y, z: v.z };
    });
    addImplementation('vectorAdd', (i) => {
        const a = vec(i.a);
        const b = vec(i.b);
        return { result: { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z } };
    });
    addImplementation('vectorSubtract', (i) => {
        const a = vec(i.a);
        const b = vec(i.b);
        return { result: { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z } };
    });
    addImplementation('vectorScale', (i) => {
        const v = vec(i.vector);
        const s = num(i.scalar, 1);
        return { result: { x: v.x * s, y: v.y * s, z: v.z * s } };
    });
    addImplementation('vectorLength', (i) => {
        const v = vec(i.vector);
        return { result: Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) };
    });
    addImplementation('vectorNormalize', (i) => {
        const v = vec(i.vector);
        const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
        if (len === 0)
            return { result: { x: 0, y: 0, z: 0 } };
        return { result: { x: v.x / len, y: v.y / len, z: v.z / len } };
    });
    addImplementation('vectorDot', (i) => {
        const a = vec(i.a);
        const b = vec(i.b);
        return { result: a.x * b.x + a.y * b.y + a.z * b.z };
    });
    addImplementation('vectorCross', (i) => {
        const a = vec(i.a);
        const b = vec(i.b);
        return {
            result: {
                x: a.y * b.z - a.z * b.y,
                y: a.z * b.x - a.x * b.z,
                z: a.x * b.y - a.y * b.x
            }
        };
    });
    addImplementation('vectorDistance', (i) => {
        const a = vec(i.a);
        const b = vec(i.b);
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dz = a.z - b.z;
        return { result: Math.sqrt(dx * dx + dy * dy + dz * dz) };
    });
    addImplementation('vectorLerp', (i) => {
        const a = vec(i.a);
        const b = vec(i.b);
        const t = num(i.alpha, 0.5);
        return {
            result: {
                x: a.x + (b.x - a.x) * t,
                y: a.y + (b.y - a.y) * t,
                z: a.z + (b.z - a.z) * t
            }
        };
    });
}
function addStringImplementations() {
    addImplementation('concat', (i) => ({ result: str(i.a) + str(i.b) }));
    addImplementation('stringLength', (i) => ({ result: str(i.value).length }));
    addImplementation('toUpper', (i) => ({ result: str(i.value).toUpperCase() }));
    addImplementation('toLower', (i) => ({ result: str(i.value).toLowerCase() }));
    addImplementation('trim', (i) => ({ result: str(i.value).trim() }));
    addImplementation('contains', (i) => ({ result: str(i.haystack).includes(str(i.needle)) }));
    addImplementation('replace', (i) => ({
        result: str(i.value).split(str(i.find)).join(str(i.replace))
    }));
    addImplementation('numberToString', (i) => ({ result: String(num(i.value)) }));
    addImplementation('booleanToString', (i) => ({ result: String(bool(i.value)) }));
    addImplementation('format', (i) => {
        let template = str(i.template, '{a}');
        for (const key of ['a', 'b', 'c', 'd']) {
            const token = `{${key}}`;
            if (!template.includes(token))
                continue;
            const replacement = i[key] == null ? '' : String(i[key]);
            template = template.split(token).join(replacement);
        }
        return { result: template };
    });
}
function addLiteralImplementations() {
    addImplementation('number', (i) => ({ value: num(i.value ?? i.number) }));
    addImplementation('string', (i) => ({ value: str(i.value ?? i.string) }));
    addImplementation('boolean', (i) => ({ value: bool(i.value ?? i.boolean) }));
    addImplementation('vector3', (i) => ({
        value: { x: num(i.x), y: num(i.y), z: num(i.z) }
    }));
    addImplementation('objectRef', (i) => ({
        value: i.value ?? { id: i.id, name: i.name, type: i.type, spotarId: i.spotarId }
    }));
    addImplementation('materialRef', (i) => ({ value: i.value ?? i }));
    addImplementation('audioRef', (i) => ({ value: i.value ?? i }));
    addImplementation('videoRef', (i) => ({ value: i.value ?? i }));
    addImplementation('assetRef', (i) => ({ value: i.value ?? i }));
    addImplementation('animationClip', (i) => ({ value: i.value ?? '' }));
}
addMathImplementations();
addComparisonImplementations();
addBooleanImplementations();
addFlowImplementations();
addVectorImplementations();
addStringImplementations();
addLiteralImplementations();
export const VALUE_NODE_IMPLEMENTATIONS = implementations;
